import { useEffect, useMemo } from 'react'
import { useAppDispatch } from '../../../../../store/hooks'
import { setAnalysis } from '../../../../../store/slices/analysisSlice'
import type { KeyMetric, VerificationCategory, ValidationItem } from '../../../../../store/slices/analysisSlice'
import type { SemanticExtraction } from '../../../../ai-viewer/eventing/extraction-types'
import type { WorkItem } from '../../../../../store/slices/workSlice'

interface UseAnalysisBuilderParams {
  semanticExtraction: SemanticExtraction | null
  workItem: WorkItem | null
  documentId?: string
}

export function useAnalysisBuilder({
  semanticExtraction,
  workItem,
  documentId
}: UseAnalysisBuilderParams) {
  const dispatch = useAppDispatch()

  // Memoize the analysis data computation
  const analysisData = useMemo(() => {
    if (!semanticExtraction || !workItem?.id || workItem.status !== 'completed') {
      return null
    }

    // Get validation data from work item outputs
    const validationReport = workItem?.outputs?.validationReport
    const statistics = validationReport?.statistics || {}
    const correctness = validationReport?.correctness || {}
    const validationResults = validationReport?.validation_results || {}
    
    // Build Key Metrics from validation statistics
    const keyMetrics: KeyMetric[] = [
      { metric: 'Accounts Count', value: statistics.accounts_count || 1 },
      { metric: 'Bank Info Present', value: statistics.bank_info_present ?? !!semanticExtraction.account_info },
      { metric: 'Company Info Present', value: statistics.company_info_present ?? true },
      { metric: 'Transaction Count', value: statistics.transaction_count || semanticExtraction.transactions.length }
    ]
    
    // Build Verification data (correctness percentages from validation)
    let balancesCorrectness = correctness.balances ?? 100
    if (correctness.balances_percentage_match) {
      const balancesMatch = correctness.balances_percentage_match
      if (typeof balancesMatch === 'object') {
        const accountNumbers = Object.keys(balancesMatch)
        if (accountNumbers.length > 0) {
          balancesCorrectness = balancesMatch[accountNumbers[0]]
        }
      } else {
        balancesCorrectness = balancesMatch
      }
    }
    
    const verification: VerificationCategory[] = [
      { 
        category: 'Job Metadata', 
        correctness: correctness.job_metadata ?? 100
      },
      { 
        category: 'Balances Present', 
        correctness: correctness.balances ?? 100
      },
      { 
        category: 'Balance Calculation Match', 
        correctness: balancesCorrectness
      },
      { 
        category: 'Bank Info', 
        correctness: correctness.bank_info ?? 100
      },
      { 
        category: 'Company Info', 
        correctness: correctness.company_info ?? 100
      },
      { 
        category: 'Transactions', 
        correctness: correctness.transactions ?? 100
      },
      { 
        category: 'Statement Info', 
        correctness: correctness.statement_info ?? 100
      }
    ]
    
    // Build Validation data from validation_results
    const validation: ValidationItem[] = [
      {
        category: 'Job Metadata',
        status: validationResults.job_metadata?.status || 'PASSED',
        warnings: validationResults.job_metadata?.warnings || [],
        errors: validationResults.job_metadata?.errors || []
      },
      {
        category: 'Balances',
        status: validationResults.balances?.status || 'PASSED',
        warnings: validationResults.balances?.warnings || [],
        errors: validationResults.balances?.errors || []
      },
      {
        category: 'Calculated Balances',
        status: validationResults.calculated_balances?.status || 'PASSED',
        warnings: validationResults.calculated_balances?.warnings || [],
        errors: validationResults.calculated_balances?.errors || []
      },
      {
        category: 'Bank Info',
        status: validationResults.bank_info?.status || 'PASSED',
        warnings: validationResults.bank_info?.warnings || [],
        errors: validationResults.bank_info?.errors || []
      },
      {
        category: 'Company Info',
        status: validationResults.company_info?.status || 'PASSED',
        warnings: validationResults.company_info?.warnings || [],
        errors: validationResults.company_info?.errors || []
      },
      {
        category: 'Statement Info',
        status: validationResults.statement_info?.status || 'PASSED',
        warnings: validationResults.statement_info?.warnings || [],
        errors: validationResults.statement_info?.errors || []
      },
      {
        category: 'Transactions',
        status: validationResults.transactions?.status || 'PASSED',
        warnings: validationResults.transactions?.warnings || [],
        errors: validationResults.transactions?.errors || []
      }
    ]

    return {
      documentId: documentId || '',
      workId: workItem.id,
      status: 'complete' as const,
      keyMetrics,
      verification,
      validation,
      createdAt: Date.now(),
      updatedAt: Date.now()
    }
  }, [semanticExtraction, workItem?.id, workItem?.status, workItem?.outputs?.validationReport, documentId])

  // Dispatch analysis data when it changes
  useEffect(() => {
    if (analysisData) {
      dispatch(setAnalysis(analysisData))
    }
  }, [analysisData, dispatch])
}

